import { REVEAL_API_TIERS } from './Plan'

export type RevealQuotaLimits = {
  requests: number
  identified: number
  uniqueIdentified: number
}

// `identified` counts repeats, so it tracks `requests`: at the unique-IP figure the hidden meter
// would cut a customer off well before the companies they actually paid for.
export const apiPlanQuotas = (identifiedCompanies: number): RevealQuotaLimits => ({
  requests: identifiedCompanies * 10,
  identified: identifiedCompanies * 10,
  uniqueIdentified: identifiedCompanies,
})

// The trial is the smallest package.
export const API_TRIAL_QUOTAS = apiPlanQuotas(REVEAL_API_TIERS[1])

export type RevealUserConfig = {
  id: number
  customerId: number
  apiKey?: string
  secretApiKey?: string
  publicApiKey?: string
  publicApiKeyDomainAllowlist?: string[]
  requestQuota: number
  requestQuotaUsage: number
  identifiedQuota: number
  identifiedQuotaUsage: number
  uniqueIdentifiedIpsQuota: number
  uniqueIdentifiedIpsQuotaUsage: number
  totalIdentified: number
  excludedFields?: string[]
  createdAt: string
  extraQuotas?: RevealExtraQuota
}

export type RevealExtraQuota = {
  yearMonth: string
  requestQuota: number
  identifiedQuota: number
  uniqueIdentifiedIpsQuota: number
}

export type RevealQuotaDimension = 'requests' | 'identified' | 'uniqueIdentifiedIps'

// Reveal spells the credit fields differently from the quota fields, so the pairing lives here once.
const QUOTA_FIELDS = {
  requests: { quota: 'requestsQuota', extra: 'requestQuota' },
  identified: { quota: 'identifiedQuota', extra: 'identifiedQuota' },
  uniqueIdentifiedIps: { quota: 'uniqueIdentifiedIpsQuota', extra: 'uniqueIdentifiedIpsQuota' },
} as const

export const revealMonthlyLimit = (
  usage: Partial<Pick<RevealQuotaUsageResponse, RevealQuotaField>>,
  dimension: RevealQuotaDimension,
) => usage[QUOTA_FIELDS[dimension].quota] ?? 0

export const revealCredits = (
  usage: Partial<Pick<RevealQuotaUsageResponse, 'extraQuotas'>>,
  dimension: RevealQuotaDimension,
) => usage.extraQuotas?.[QUOTA_FIELDS[dimension].extra] ?? 0

// Credits sit beside the base quota rather than in it, so what a customer may use is the sum. Both
// are optional: a customer with no reveal config comes back carrying only their keys.
export const revealLimit = (
  usage: Partial<Pick<RevealQuotaUsageResponse, RevealQuotaField | 'extraQuotas'>>,
  dimension: RevealQuotaDimension,
) => revealMonthlyLimit(usage, dimension) + revealCredits(usage, dimension)

type RevealQuotaField = (typeof QUOTA_FIELDS)[RevealQuotaDimension]['quota']

export const hasRevealConfig = (usage: unknown): usage is RevealQuotaUsageResponse =>
  typeof usage === 'object' && usage !== null && 'requestsQuota' in usage

export type RevealQuotaUsageResponse = {
  customerId: number
  requestsQuotaUsage: number
  requestsQuota: number
  identifiedQuotaUsage: number
  identifiedQuota: number
  uniqueIdentifiedIpsQuota: number
  uniqueIdentifiedIpsQuotaUsage: number
  uniqueIdentifiedCompaniesUsage?: number
  apiKey?: string
  secretApiKey?: string | null
  publicApiKey?: string | null
  publicApiKeyDomainAllowlist?: string[]
  extraQuotas?: RevealExtraQuota
}

// Note: the reveal PUT uses `uniqueIdentifiedQuota` while reads return `uniqueIdentifiedIpsQuota`.
export type PutRevealExtraQuotaRequest = {
  yearMonth: string
  requestQuota: number
  identifiedQuota: number
  uniqueIdentifiedQuota: number
}

export type CreateRevealUserConfigRequest = {
  customerId: number
  requestQuota: number
  identifiedQuota: number
  uniqueIdentifiedQuota: number
  excludedFields?: string[]
}

export type PutRevealUserConfigRequest = {
  apiKey?: string
  secretApiKey?: string
  publicApiKey?: string
  publicApiKeyDomainAllowlist?: string[]
  requestQuota?: number
  identifiedQuota?: number
  uniqueIdentifiedIpsQuota?: number
  excludedFields?: string[]
}

export type PatchRevealUserConfigRequest = {
  publicApiKeyDomainAllowlist?: string[]
  requestQuota?: number
  identifiedQuota?: number
  uniqueIdentifiedIpsQuota?: number
  excludedFields?: string[]
}

export type RevealApiKeyType = 'PublicApiKey' | 'SecretApiKey'
